import {useState, useEffect} from 'react';
import isClient from '../utils/isClient';
import {isInIframe} from '../utils/iFrameNotifyer/utils';

/**
 * Returns the actual viewport height when running inside an iframe.
 * In iframes on mobile, CSS 100dvh resolves to the full browser viewport,
 * not the iframe dimensions. This hook uses window.innerHeight which
 * correctly reports the iframe's inner height.
 *
 * @returns viewport height in pixels, or null when not in an iframe
 */
export default function useIframeViewportHeight(): number | null {
    const [isIframe] = useState(() => isInIframe());
    const [viewportHeight, setViewportHeight] = useState<number | null>(() => {
        if (!isClient || !isIframe) {
            return null;
        }
        return window.innerHeight;
    });

    useEffect(() => {
        if (!isClient || !isIframe) {
            return;
        }

        function handleResize() {
            setViewportHeight(window.innerHeight);
        }

        window.addEventListener('resize', handleResize);
        window.visualViewport?.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [isIframe]);

    return isIframe ? viewportHeight : null;
}
